import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from '../../utils/i18nStub';
import './EmployeeProfile.css';
import EmployeePersonalInfo from './EmployeePersonalInfo';
import EmployeeContactInfo from './EmployeeContactInfo';
import EmployeeAddressInfo from './EmployeeAddressInfo'; // ← Address tab component
import EmployeeEmploymentInfo from './EmployeeEmploymentInfo';
import EmployeeBankInfo from './EmployeeBankInfo';
import EmployeeLanguagesInfo from './EmployeeLanguagesInfo';
import EmployeeEducationInfo from './EmployeeEducationInfo';
import EmployeeOtherEmployment from './EmployeeOtherEmployment';
import EmployeeMosqueData from './EmployeeMosqueData';
import EmployeeJameData from './EmployeeJameData';

import { getReviewSnapshot } from '../../services/employeeService'; // server snapshot
import { fetchRegions, fetchCitiesByRegion } from '../../services/geoService';
import { fetchEmploymentLookupMaps } from '../../services/employmentLookupsService';

import {
  CCard,
  CCardBody,
  CSpinner,
  CRow,
  CCol,
  CButton,
  CFormSelect,
} from '@coreui/react';

import { getYaqeenInfo, verifyYaqeen } from '../../services/employeeService';

function EmployeeProfile() {
  const { t, i18n } = useTranslation('profile');
  const [yaqeenInfo, setYaqeenInfo] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(true);

  const toggleAccordion = () => setAccordionOpen((prev) => !prev);

  // ---------- Child refs (each tab exposes refreshOverlay/getOverlaySnapshot) ----------
  const empInfoRef = useRef(null);
  const contactRef = useRef(null);
  const addressRef = useRef(null); // Address ref
  const employmentRef = useRef(null);

  const [nationalId, setNationalId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // ---------- Review modal state ----------
  const [, setShowReview] = useState(false);
  const [declared, setDeclared] = useState(false);

  // Overlays rendered in the modal (server snapshot)
  const [, setReview] = useState({ personal: null, contact: null, address: null, employment: null });

  // Server meta + request_vals for the modal (server-truth)
  const [, setReviewMeta] = useState({ requestStatus: 'none', pendingCount: 0, lastSavedAt: null });
  const [, setServerRequestVals] = useState(null);
  const [, setLoadingReview] = useState(false);

  const [regionNameById, setRegionNameById] = useState({});
  const [cityNameById, setCityNameById]     = useState({});

  const [jobNameById, setJobNameById] = useState({});
  const [relationNameByCode, setRelationNameByCode] = useState({});

  const mapById = (items) =>
    (items || []).reduce((acc, it) => { acc[it.id] = it.name; return acc; }, {});

  const ensureGeoNames = async (regionId) => {
    // Regions: load once
    if (Object.keys(regionNameById).length === 0) {
      const regions = await fetchRegions();            // [{ id, name }]
      setRegionNameById(mapById(regions));
    }

    // Cities: load for the region in the snapshot (if any)
    if (regionId && !Object.keys(cityNameById).length) {
      const cities = await fetchCitiesByRegion(regionId); // [{ id, name }]
      setCityNameById(prev => ({ ...prev, ...mapById(cities) }));
    }
  };

  const ensureEmploymentNames = useCallback(async () => {
    if (Object.keys(jobNameById).length && Object.keys(relationNameByCode).length) return;
    const maps = await fetchEmploymentLookupMaps(i18n.language);
    if (!Object.keys(jobNameById).length) setJobNameById(maps.jobNameById);
    if (!Object.keys(relationNameByCode).length) setRelationNameByCode(maps.relationNameByCode);
  }, [jobNameById, relationNameByCode, i18n.language]);

  // ---------- Initial data (Yaqeen panel) ----------
  useEffect(() => {
    if (loaded) return;
    const fetchData = async () => {
      try {
        const res = await getYaqeenInfo();
        setYaqeenInfo(res);
      } catch {
        console.warn('Yaqeen info not found.');
      } finally {
        setLoaded(true);
      }
    };
    fetchData();
  }, [loaded]);

  // ---------- Yaqeen verify ----------
  const handleVerify = async () => {
    if (!nationalId || !dateOfBirth) {
      setErrorMsg(t('yaqeen.missing_input'));
      return;
    }
    setVerifying(true);
    setErrorMsg('');
    try {
      await verifyYaqeen({ identificationId: nationalId, dateOfBirth });
      setLoaded(false); // trigger re-fetch of Yaqeen info
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t('yaqeen.verification_failed'));
    } finally {
      setVerifying(false);
    }
  };

  const emptyOverlay = () => ({
    data: {},
    flags: {},
    pendingCount: 0,
    pendingCountTab: 0,
    baselinePresent: false,
    lastSavedAt: null,
  });

  // ---------- Review modal: open + hydrate snapshot ----------
  const handleOpenReview = async () => {
    setDeclared(false);
    setLoadingReview(true);
    try {
      // 1) server snapshot (has personal/contact/address/employment)
      const snap = await getReviewSnapshot();

      // 2) meta + server-built request_vals
      setReviewMeta({
        requestStatus: snap?.meta?.requestStatus ?? 'none',
        pendingCount:  snap?.meta?.pendingCount ?? 0,
        lastSavedAt:   snap?.meta?.lastSavedAt ?? null,
      });
      setServerRequestVals(snap?.request_vals ?? null);

      // 3) bind ALL overlays from the snapshot
      const overlays = {
        personal:   snap?.overlays?.personal   ?? emptyOverlay(),
        contact:    snap?.overlays?.contact    ?? emptyOverlay(),
        address:    snap?.overlays?.address    ?? emptyOverlay(),
        employment: snap?.overlays?.employment ?? emptyOverlay(),
      };

      // 4) preload geo & employment names for display (keeps IDs for submit)
      const regionId = overlays.address?.data?.region_id;
      await ensureGeoNames(regionId);
      await ensureEmploymentNames();

      setReview(overlays);

      // 5) open the modal
      setShowReview(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingReview(false);
    }
  };

  // ---------- Yaqeen block (unchanged) ----------
  const renderYaqeen = () => {
    if (!yaqeenInfo) {
      const currentYear = new Date().getFullYear();
      const hijriOffset = 579;
      // Always use Hijri years for the year dropdown
      const years = Array.from({ length: 150 }, (_, i) => currentYear - i - hijriOffset);

      const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
      const hijriMonths = {
        '01': 'محرم','02': 'صفر','03': 'ربيع الأول','04': 'ربيع الثاني','05': 'جمادى الأولى','06': 'جمادى الآخرة',
        '07': 'رجب','08': 'شعبان','09': 'رمضان','10': 'شوال','11': 'ذو القعدة','12': 'ذو الحجة'
      };
      const [yearValue = '', monthValue = ''] = dateOfBirth.split('-');

      return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end text-right">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-500 mb-1">{t('personal.identification_id', 'رقم الهوية الوطنية')}</span>
              <input
                value={nationalId || ''}
                readOnly
                disabled
                className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-500 mb-1">{t('yaqeen.year', 'السنة')}</span>
              <CFormSelect
                value={yearValue}
                onChange={(e) => setDateOfBirth(`${e.target.value || ''}-${monthValue || ''}`)}
                className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                dir="rtl"
              >
                <option value="">--</option>
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </CFormSelect>
            </div>

            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-500 mb-1">{t('yaqeen.month', 'الشهر')}</span>
              <CFormSelect
                value={monthValue}
                onChange={(e) => setDateOfBirth(`${yearValue || ''}-${e.target.value || ''}`)}
                className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                dir="rtl"
              >
                <option value="">--</option>
                {months.map((m) => (
                  <option key={m} value={m}>{`${m} - ${hijriMonths[m]}`}</option>
                ))}
              </CFormSelect>
            </div>
          </div>

          <div className="flex justify-end">
            <CButton
              color="primary"
              onClick={handleVerify}
              disabled={verifying || !(yearValue && monthValue)}
              className="px-6"
            >
              {verifying ? <CSpinner size="sm" /> : t('yaqeen.fetch', 'تحقق')}
            </CButton>
          </div>

          {errorMsg && <p className="text-sm text-red-600 text-right">{errorMsg}</p>}
        </div>
      );
    }

    const fullName = `${yaqeenInfo.firstName || ''} ${yaqeenInfo.fatherName || ''} ${yaqeenInfo.grandFatherName || ''} ${yaqeenInfo.familyName || ''}`.trim();
    const gregorianDOB = yaqeenInfo.idIssueDate
      ? new Date(yaqeenInfo.idIssueDate).toLocaleDateString('ar-SA')
      : '—';

    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-right">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('personal.identification_id', 'رقم الهوية الوطنية')}</span>
            <span className="text-gray-900 font-medium">{yaqeenInfo.identificationId || nationalId || '—'}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('yaqeen.name', 'الاسم')}</span>
            <span className="text-gray-900 font-medium">{fullName || '—'}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('yaqeen.gender', 'الجنس')}</span>
            <span className="text-gray-900 font-medium">{yaqeenInfo.gender || '—'}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('yaqeen.gregorian_birth', 'الميلاد (ميلادي)')}</span>
            <span className="text-gray-900 font-medium">{gregorianDOB}</span>
          </div>
        </div>
      </div>
    );
  };

  // ---------- Tab titles ----------
  const tabTitles = [
    'البيانات الشخصية',
    'بيانات التواصل',
    'عنوان السكن',
    'بيانات الوظيفة',
    'بيانات المسجد',
    'بيانات الجامع',
    'خدمات المسجد',
    'جهات عمل أخرى',
    'المؤهلات العلمية',
    'اللغات',
    'الحساب البنكي',
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="space-y-6">
            {renderYaqeen()}
            <EmployeePersonalInfo
              active
              ref={empInfoRef}
              onLoadNationalId={(id) => {
                setNationalId(id);
              }}
            />
          </div>
        );
      case 1:
        return (
          <EmployeeContactInfo
            active
            ref={contactRef}
          />
        );
      case 2:
        return (
          <EmployeeAddressInfo
            active
            ref={addressRef}
          />
        );
      case 3:
        return (
          <EmployeeEmploymentInfo
            active
            ref={employmentRef}
          />
        );
      case 4:
        return <EmployeeMosqueData />;
      case 5:
        return <EmployeeJameData />;
      case 7:
        return <EmployeeOtherEmployment />;
      case 8:
        return <EmployeeEducationInfo />;
      case 9:
        return <EmployeeLanguagesInfo />;
      case 10:
        return <EmployeeBankInfo />;
      default:
        return (
          <CRow>
            <CCol>
              <p className="text-muted">
                {t('tabs.content_placeholder', 'سيتم إتاحة هذا القسم قريباً')} {tabTitles[activeTab]}
              </p>
            </CCol>
          </CRow>
        );
    }
  };

  return (
    <div dir="rtl" className="bg-gray-50">
      <div className="employee-profile-container max-w-7xl mx-auto px-4 md:px-6 py-16">
        <CCard className="mb-4">
          {/* Removed CCardHeader with title to eliminate the green bar */}
          <CCardBody style={{ paddingTop: '2rem' }}>
            {!loaded ? (
              <CSpinner color="primary" />
            ) : (
              <>
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Side navigation */}
                  <aside className="lg:w-72 flex-shrink-0">
                    <div className="hidden lg:block bg-white/80 border border-gray-200 rounded-2xl shadow-sm p-3 space-y-2">
                      {tabTitles.map((title, idx) => {
                        const isActive = activeTab === idx;
                        return (
                          <button
                            key={title}
                            type="button"
                            onClick={() => setActiveTab(idx)}
                            className={`w-full text-right text-sm font-semibold rounded-xl px-4 py-3 transition-all duration-200 ${
                              isActive
                                ? 'bg-sa-100 text-sa-700 shadow-inner border border-sa-200'
                                : 'text-gray-700 hover:bg-gray-100 border border-transparent'
                            }`}
                          >
                            {title}
                          </button>
                        );
                      })}
                    </div>

                    <div className="lg:hidden bg-white/80 border border-gray-200 rounded-2xl shadow-sm">
                      <button
                        type="button"
                        onClick={toggleAccordion}
                        className="w-full flex items-center justify-between px-4 py-3 text-right text-sm font-semibold text-gray-700"
                      >
                        <span>{tabTitles[activeTab]}</span>
                        <svg
                          className={`w-5 h-5 transition-transform duration-200 ${accordionOpen ? 'rotate-180' : ''}`}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>

                      {accordionOpen && (
                        <div className="border-t border-gray-200">
                          {tabTitles.map((title, idx) => {
                            const isActive = activeTab === idx;
                            return (
                              <button
                                key={title}
                                type="button"
                                onClick={() => {
                                  setActiveTab(idx);
                                  setAccordionOpen(false);
                                }}
                                className={`w-full text-right text-sm font-semibold px-4 py-3 transition-all duration-200 ${
                                  isActive ? 'text-sa-700 bg-sa-50' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {title}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </aside>

                  {/* Content area */}
                  <div className="flex-1">
                    {/* -------- Tabs content -------- */}
                    <div className="bg-white/80 border border-gray-200 rounded-2xl shadow-sm p-4 transition-all duration-200">
                      {renderActiveTab()}
                    </div>

                    {/* -------- Review button -------- */}
                    <div className="mt-6 flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-2xl px-4 py-3">
                      <label className="flex items-center gap-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={declared}
                          onChange={(e) => setDeclared(e.target.checked)}
                          className="h-5 w-5 rounded border-gray-300 text-sa-600 focus:ring-sa-500"
                        />
                        {t('common:declaration_text', 'أُقرّ بأن المعلومات أعلاه صحيحة وسيتم إرسالها للتحديث في النظام.')}
                      </label>
                      <CButton color="primary" disabled={!declared} onClick={handleOpenReview}>
                        {t('common:review_and_submit', 'مراجعة وإرسال')}
                      </CButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CCardBody>
        </CCard>
      </div>
    </div>
  );
}

export default EmployeeProfile;
